import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

function Sidebar({ appName, currentVersion, sections, onSectionChange, activeSection }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  const handleLogout = async () => {
    try {
      await logout();
      window.location.reload();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const mainSections = [
    { id: 'dashboard', label: 'Dashboard', path: '/', icon: '📊' },
    { id: 'appstore', label: 'App Store', path: '/appstore', icon: '🏪' },
    { id: 'developer', label: 'Developer', path: '/developer', icon: '⚙️' },
  ];

  const getVersionState = () => {
    const state = currentVersion?.attributes?.appStoreState;
    if (!state) return null;
    
    const stateMap = {
      'PREPARE_FOR_SUBMISSION': { label: 'Prepare', color: 'warning' },
      'WAITING_FOR_REVIEW': { label: 'Waiting', color: 'primary' },
      'IN_REVIEW': { label: 'In Review', color: 'primary' },
      'READY_FOR_SALE': { label: 'Live', color: 'success' },
      'DEVELOPER_REJECTED': { label: 'Rejected', color: 'error' },
    };
    
    return stateMap[state] || { label: state, color: 'neutral' };
  };

  const versionState = getVersionState();

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <div className="sidebar-logo">🍎</div>
          <span className="sidebar-title">App Store Connect</span>
        </div>
        
        {appName && (
          <div className="sidebar-app-info">
            <div className="sidebar-app-name">{appName}</div>
            {currentVersion && (
              <div className="sidebar-version">
                <span className="version-value">
                  v{currentVersion.attributes?.versionString}
                  {versionState && (
                    <span className={`version-badge badge-${versionState.color}`}>
                      {versionState.label}
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <nav className="sidebar-nav">
        <div className="nav-section">
          <div className="nav-section-title">Navigation</div>
          {mainSections.map(section => (
            <button
              key={section.id}
              className={`nav-item ${location.pathname === section.path ? 'active' : ''}`}
              onClick={() => navigate(section.path)}
            >
              <span className="nav-icon">{section.icon}</span>
              <span className="nav-label">{section.label}</span>
            </button>
          ))}
        </div>

        {sections && sections.length > 0 && location.pathname === '/appstore' && (
          <div className="nav-section">
            <div className="nav-section-title">App Store Sections</div>
            {sections.map(section => (
              <button
                key={section.id}
                className={`nav-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => onSectionChange && onSectionChange(section.id)}
              >
                <span className="nav-icon">{section.icon}</span>
                <span className="nav-label">{section.label}</span>
              </button>
            ))}
          </div>
        )}
      </nav>

      <div className="sidebar-footer">
        <div className="theme-toggle">
          <span className="theme-toggle-label">
            {theme === 'dark' ? '🌙' : '☀️'} {theme === 'dark' ? 'Dark' : 'Light'}
          </span>
          <label className="theme-switch">
            <input
              type="checkbox"
              checked={theme === 'dark'}
              onChange={toggleTheme}
            />
            <span className="theme-switch-slider"></span>
          </label>
        </div>
        <button className="btn btn-ghost btn-block" onClick={handleLogout}>
          <span>↩️</span> Sign Out
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;